// ============================================
// Account Detail Page - Based on approved views/account-detail.html
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DetailPageSkeleton } from '@/components/skeletons';
import { ACCOUNT_TYPE, type AccountType, type Industry, type UpdateAccount } from '@muraka/shared';
import { ArrowLeft, Edit, Trash2, Loader2, Plus } from 'lucide-react';

const typeConfig: Record<AccountType, { label: string; class: string }> = {
  prospect: { label: 'Prospect', class: 'bg-blue-100 text-blue-700' },
  customer: { label: 'Customer', class: 'bg-green-100 text-green-700' },
  partner: { label: 'Partner', class: 'bg-amber-100 text-amber-700' },
  competitor: { label: 'Competitor', class: 'bg-red-100 text-red-700' },
};

const industryLabels: Record<Industry, string> = {
  hotel: 'Hotel',
  resort: 'Resort',
  guesthouse: 'Guest House',
  safari_vessel: 'Safari Vessel',
  cruise: 'Cruise',
  restaurant: 'Restaurant',
  government: 'Government',
  healthcare: 'Healthcare',
  retail: 'Retail',
  corporate: 'Corporate',
  distributor: 'Distributor',
  other: 'Other',
};

const stageConfig: Record<string, { label: string; class: string }> = {
  qualification: { label: 'Qualification', class: 'stage-qualification' },
  needs_analysis: { label: 'Needs Analysis', class: 'stage-needs-analysis' },
  proposal: { label: 'Proposal', class: 'stage-proposal' },
  negotiation: { label: 'Negotiation', class: 'stage-negotiation' },
  closed_won: { label: 'Closed Won', class: 'stage-closed-won' },
  closed_lost: { label: 'Closed Lost', class: 'stage-closed-lost' },
};

const iconColors = [
  'bg-blue-100 text-blue-600',
  'bg-green-100 text-green-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-red-100 text-red-600',
  'bg-pink-100 text-pink-600',
];

const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

function getIconColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return iconColors[Math.abs(hash) % iconColors.length];
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function getInitials(firstName: string, lastName?: string): string {
  if (lastName) {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }
  const parts = firstName.split(' ');
  return (
    parts
      .map(p => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U'
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(date: string): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatFullDate(date: string): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AccountDetailPage() {
  const { id } = useParams({ from: '/accounts/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState<'details' | 'contacts' | 'opportunities'>('details');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: ACCOUNT_TYPE.PROSPECT as AccountType,
    industry: '' as Industry | '',
    website: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    notes: '',
  });

  // Query account
  const {
    data: account,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['account', id],
    queryFn: () => api.getAccount(id),
    enabled: !!id,
  });

  // Query contacts
  const { data: contacts } = useQuery({
    queryKey: ['account-contacts', id],
    queryFn: () => api.getAccountContacts(id),
    enabled: !!id,
  });

  // Query opportunities
  const { data: opportunities } = useQuery({
    queryKey: ['account-opportunities', id],
    queryFn: () => api.getAccountOpportunities(id),
    enabled: !!id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateAccount) => api.updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setEditModalOpen(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      navigate({ to: '/accounts' });
    },
  });

  const openEditModal = () => {
    if (account) {
      setFormData({
        name: account.name,
        type: account.type as AccountType,
        industry: (account.industry as Industry) || '',
        website: account.website || '',
        phone: account.phone || '',
        address: account.address || '',
        city: account.city || '',
        state: account.state || '',
        country: account.country || '',
        postalCode: account.postalCode || '',
        notes: account.notes || '',
      });
      setEditModalOpen(true);
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: UpdateAccount = {
      name: formData.name,
      type: formData.type,
      industry: (formData.industry as Industry) || null,
      website: formData.website || null,
      phone: formData.phone || null,
      address: formData.address || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      postalCode: formData.postalCode || null,
      notes: formData.notes || null,
    };
    updateMutation.mutate(updateData);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
          <DetailPageSkeleton />
        </div>
      </AppLayout>
    );
  }

  if (error || !account) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className="text-destructive font-medium mb-2">Failed to load account</p>
          <p className="text-sm text-muted-foreground mb-4">
            The account may have been deleted or you don't have access.
          </p>
          <Button variant="outline" onClick={() => navigate({ to: '/accounts' })}>
            Back to Accounts
          </Button>
        </div>
      </AppLayout>
    );
  }

  const typeInfo = typeConfig[account.type as AccountType];

  return (
    <AppLayout>
      {/* Account Header Section */}
      <div className="bg-card border-b border-border -mx-4 -mt-4 lg:-mx-6 lg:-mt-6">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Back Button */}
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate({ to: '/accounts' })}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Accounts</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-4">
            {/* Account Info */}
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-lg flex items-center justify-center shrink-0 ${getIconColor(account.name)}`}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">{account.name}</h1>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeInfo.class}`}
                  >
                    {typeInfo.label}
                  </span>
                </div>
                <p className="text-muted-foreground mt-1">
                  {account.industry
                    ? industryLabels[account.industry as Industry]
                    : 'No industry specified'}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  {account.website && (
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                        />
                      </svg>
                      <a
                        href={account.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary"
                      >
                        {account.website.replace('https://', '').replace('http://', '')}
                      </a>
                    </span>
                  )}
                  {account.phone && (
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <a href={`tel:${account.phone}`} className="hover:text-primary">
                        {account.phone}
                      </a>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Edit className="w-4 h-4" />
                Edit
              </Button>
              <Button variant="outline" size="icon" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'contacts'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Contacts ({contacts?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('opportunities')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'opportunities'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Opportunities ({opportunities?.length || 0})
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto py-6">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content (2 columns) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Account Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Account Information</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  <dl className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm text-muted-foreground">Account Name</dt>
                      <dd className="mt-1 text-foreground">{account.name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Type</dt>
                      <dd className="mt-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeInfo.class}`}
                        >
                          {typeInfo.label}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Website</dt>
                      <dd className="mt-1">
                        {account.website ? (
                          <a
                            href={account.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {account.website}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Phone</dt>
                      <dd className="mt-1">
                        {account.phone ? (
                          <a href={`tel:${account.phone}`} className="text-primary hover:underline">
                            {account.phone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Industry</dt>
                      <dd className="mt-1 text-foreground">
                        {account.industry ? industryLabels[account.industry as Industry] : '—'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm text-muted-foreground">Address</dt>
                      <dd className="mt-1 text-foreground">
                        {account.address ||
                        account.city ||
                        account.state ||
                        account.postalCode ||
                        account.country
                          ? [
                              account.address,
                              [account.city, account.state, account.postalCode]
                                .filter(Boolean)
                                .join(', '),
                              account.country,
                            ]
                              .filter(Boolean)
                              .join('\n')
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Notes Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Notes</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  {account.notes ? (
                    <p className="text-foreground whitespace-pre-wrap">{account.notes}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No notes added yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar (1 column) */}
            <div className="space-y-6">
              {/* Statistics Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Statistics</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Contacts</dt>
                    <dd className="mt-1 text-2xl font-semibold text-foreground">
                      {account.contactCount || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Open Opportunities</dt>
                    <dd className="mt-1 text-2xl font-semibold text-foreground">
                      {account.opportunityCount || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Total Pipeline</dt>
                    <dd className="mt-1 text-2xl font-semibold text-foreground">
                      {formatCurrency(account.totalOpportunityValue || 0)}
                    </dd>
                  </div>
                </div>
              </div>

              {/* System Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">System Information</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Created</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(account.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(account.updatedAt)}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-card rounded-xl border border-border">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">Contacts</h2>
              <Button
                size="sm"
                variant="outline"
                className="text-primary"
                onClick={() => navigate({ to: '/contacts' })}
              >
                <Plus className="w-4 h-4" />
                Add Contact
              </Button>
            </div>
            <div className="p-6">
              {!contacts || contacts.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-6 h-6 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-muted-foreground">No contacts yet</p>
                  <Link
                    to="/contacts"
                    className="text-sm text-primary hover:underline mt-2 inline-block"
                  >
                    Add a contact
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {contacts.map(contact => {
                    const fullName = `${contact.firstName} ${contact.lastName}`;
                    return (
                      <Link
                        key={contact.id}
                        to="/contacts/$id"
                        params={{ id: contact.id }}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${getAvatarColor(fullName)}`}
                          >
                            <span className="text-sm font-medium">
                              {getInitials(contact.firstName, contact.lastName)}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{fullName}</p>
                              {contact.isPrimary && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {contact.title || 'No title'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-sm hidden sm:block">
                          <p className="text-muted-foreground">{contact.email || ''}</p>
                          <p className="text-muted-foreground">{contact.phone || ''}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Opportunities Tab */}
        {activeTab === 'opportunities' && (
          <div className="bg-card rounded-xl border border-border">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">Opportunities</h2>
              <Button
                size="sm"
                variant="outline"
                className="text-primary"
                onClick={() => navigate({ to: '/opportunities' })}
              >
                <Plus className="w-4 h-4" />
                Add Opportunity
              </Button>
            </div>
            <div className="p-6">
              {!opportunities || opportunities.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-6 h-6 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  <p className="text-muted-foreground">No opportunities yet</p>
                  <Link
                    to="/opportunities"
                    className="text-sm text-primary hover:underline mt-2 inline-block"
                  >
                    Create an opportunity
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {opportunities.map(opp => {
                    const stage = stageConfig[opp.stage] || {
                      label: opp.stage,
                      class: 'bg-muted text-muted-foreground',
                    };
                    return (
                      <Link
                        key={opp.id}
                        to="/opportunities/$id"
                        params={{ id: opp.id }}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                      >
                        <div>
                          <p className="font-medium text-foreground">{opp.name}</p>
                          <p className="text-sm text-muted-foreground">Stage: {stage.label}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-foreground">
                            {formatCurrency(opp.amount)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Close: {formatDate(opp.expectedCloseDate)}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <DialogContent onClose={() => setEditModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-6 p-6 pt-0 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Account Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Account Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Account Name"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Acme Corporation"
                    className="sm:col-span-2"
                  />
                  <Select
                    label="Account Type"
                    required
                    value={formData.type}
                    onChange={e =>
                      setFormData({ ...formData, type: e.target.value as AccountType })
                    }
                  >
                    {Object.entries(typeConfig).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Industry"
                    value={formData.industry}
                    onChange={e =>
                      setFormData({ ...formData, industry: e.target.value as Industry })
                    }
                  >
                    <option value="">Select Industry</option>
                    {Object.entries(industryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Website"
                    type="url"
                    value={formData.website}
                    onChange={e => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://company.com"
                  />
                  <Input
                    label="Phone"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Address
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Street Address"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    className="sm:col-span-2"
                  />
                  <Input
                    label="City"
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    placeholder="San Francisco"
                  />
                  <Input
                    label="State/Province"
                    value={formData.state}
                    onChange={e => setFormData({ ...formData, state: e.target.value })}
                    placeholder="California"
                  />
                  <Input
                    label="Country"
                    value={formData.country}
                    onChange={e => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                  />
                  <Input
                    label="Postal Code"
                    value={formData.postalCode}
                    onChange={e => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="94102"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this account..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogContent onClose={() => setDeleteModalOpen(false)} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              Delete Account?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">{account.name}</span>? All associated
              contacts and opportunities must be removed first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
